import { DropDownListComponent } from "@syncfusion/ej2-react-dropdowns";
import {
  ColumnDirective,
  ColumnsDirective,
  ExcelExport,
  GridComponent,
  Inject,
  Page,
  Resize,
  Search,
  Sort,
  Toolbar,
} from "@syncfusion/ej2-react-grids";
import { NumericTextBoxComponent } from "@syncfusion/ej2-react-inputs";
import { Query } from "@syncfusion/ej2-data";

import axios from "axios";
import { getCookie } from "cookies-next";
import { useEffect, useRef, useState } from "react";
import { FaEye, FaPlus, FaTrash, FaXmark } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { FaEdit, FaSync } from "react-icons/fa";
import { Header, PageLoading } from "../../../components";
import { API_ROOT } from "../../../config";
import { useStateContext } from "../../../contexts/ContextProvider";
import "../../table.css";

const DocumentTypes = () => {
  const navigate = useNavigate();
  const { currentColor } = useStateContext();

  const [pageLoading, setPageLoading] = useState(true);
  const [documentTypesList, setDocumentTypesList] = useState([]);
  const [refresh, setRefresh] = useState(0);
  const [toggleAdd, setToggleAdd] = useState(false);
  const [toggleEdit, setToggleEdit] = useState(false);
  const [toggleDetail, setToggleDetail] = useState(false);
  const [toggleDelete, setToggleDelete] = useState(false);
  const [toggleSync, setToggleSync] = useState(false);
  const [isDetailMode, setIsDetailMode] = useState(false);

  // Master data
  const [membershipTypes, setMembershipTypes] = useState([]);
  const [docSizes, setDocSizes] = useState([]);
  const [integratedPlatforms, setIntegratedPlatforms] = useState([]);
  const [jenisOrders, setJenisOrders] = useState([]);

  const [documentTypeData, setDocumentTypeData] = useState({
    uuid: null,
    membership_type_id: null,
    name: null,
    orientation: "portrait",
    bg_color: "ffffff",
    docSizeId: 3,
    price: 0,
    is_active: false,
    show_timestamp: false,
    jenis_order_id: null,
  });

  const [documentTypeWarning, setDocumentTypeWarning] = useState({
    membership_type_id: null,
    name: null,
    orientation: null,
    docSizeId: null,
    price: null,
    is_active: null,
    show_timestamp: null,
    jenis_order_id: null,
  });

  const [syncDocumentTypeData, setSyncDocumentTypeData] = useState({
    integrated_platform_id: null,
  });

  const [syncDocumentTypeWarning, setSyncDocumentTypeWarning] = useState({
    integrated_platform_id: null,
  });

  const [documentTypeDisabled, setDocumentTypeDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const gridRef = useRef(null);

  // Dropdown options
  const orientationOptions = [
    { text: "Portrait", value: "portrait" },
    { text: "Landscape", value: "landscape" },
  ];

  const formatDate = (inputDate) => {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const date = new Date(inputDate);
    const dayOfWeek = days[date.getUTCDay()];
    const day = date.getUTCDate();
    const month = months[date.getUTCMonth()];
    const year = date.getUTCFullYear();
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    const seconds = date.getUTCSeconds();

    return `${dayOfWeek}, ${day} ${month} ${year} ${hours
      .toString()
      .padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const fetchMasterData = async () => {
    try {
      // Fetch document sizes
      const docSizesResponse = await axios.get(
        API_ROOT + "/admin/document-types/document-sizes/list",
        {
          headers: { Authorization: getCookie("admin_auth") },
        },
      );
      setDocSizes(
        docSizesResponse.data.data.map((item) => ({
          text: item.name,
          value: item.id,
        })),
      );

      // Fetch integrated platforms
      const platformResponse = await axios.get(
        API_ROOT + "/admin/user/integrated-platform/list?offset=1&limit=all",
        {
          headers: { Authorization: getCookie("admin_auth") },
        },
      );
      setIntegratedPlatforms(
        platformResponse.data.data.map((item) => ({
          text: item.platform_name,
          value: item.uuid,
        })),
      );

      // Fetch Jenis Orders
      const response = await axios.get(API_ROOT + "/admin/select/jenis-order", {
        headers: { Authorization: getCookie("admin_auth") },
      });

      setJenisOrders(
        response.data.data.map((item) => ({
          text: item.jenis_layanan,
          value: item.id,
        })),
      );
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/login");
      }
    }
  };

  const fetchMembershipTypesByIntegratedPlatform = async (
    integratedPlatformId,
  ) => {
    try {
      const membershipResponse = await axios.get(
        API_ROOT +
          `/admin/select/membership-by-integrated-platform/${integratedPlatformId}`,
        {
          headers: { Authorization: getCookie("admin_auth") },
        },
      );
      setMembershipTypes(
        membershipResponse.data.data.map((item) => ({
          text: item.name,
          value: item.membershipTypeId,
        })),
      );
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/login");
      }
    }
  };

  const fetchData = async () => {
    try {
      const response = await axios.get(
        API_ROOT + "/admin/document-types/list?offset=1&limit=all",
        {
          headers: {
            Authorization: getCookie("admin_auth"),
          },
        },
      );

      const data = response.data.data;
      setDocumentTypesList(() =>
        data.map((item, index) => ({
          No: index + 1,
          Uuid: item.uuid,
          MembershipTypeName: item.membership_type?.name || "-",
          IntegratedPlatformName:
            item.membership_type?.integrated_platform?.platform_name || "-",
          Name: item.name_doc,
          Orientation: item.orientation,
          BgColor: item.bg_color,
          IsActive: item.is_active,
          ShowTimestamp: item.show_timestamp,
          JenisLayanan: item.jenis_order?.jenis_layanan,
          KeyDoc: item.key_doc,
          DocSizeName: item.doc_size?.name || "A4",
          Price: formatPrice(item.price),
          PriceRaw: item.price,
          CreatedAt: formatDate(item.created_at),
        })),
      );
      setPageLoading(false);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate("/login");
        return;
      }
      console.log(error);
      toast.error("Failed to get data");
      setPageLoading(false);
    }
  };

  const fetchDocumentTypeDetail = async (uuid) => {
    try {
      const response = await axios.get(
        API_ROOT + "/admin/document-types/detail/" + uuid,
        {
          headers: { Authorization: getCookie("admin_auth") },
        },
      );

      const data = response.data.data;
      setDocumentTypeData({
        uuid: data.uuid,
        membership_type_id: data.membership_type.membershipTypeId,
        integrated_platform_id: data.membership_type?.integrated_platform?.uuid,
        name: data.name_doc,
        orientation: data.orientation,
        bg_color: data.bg_color,
        docSizeId: data.doc_size_id,
        price: data.price,
        is_active: data.is_active,
        show_timestamp: data.show_timestamp,
        jenis_order_id: data.jenis_order_id,
      });

      if (data.membership_type?.integrated_platform?.uuid) {
        fetchMembershipTypesByIntegratedPlatform(
          data.membership_type?.integrated_platform?.uuid,
        );
      }
    } catch (error) {
      toast.error("Failed to fetch document type details");
      console.log(error);
    }
  };

  useEffect(() => {
    setPageLoading(true);
    setDocumentTypesList([]);
    setToggleAdd(false);
    setToggleEdit(false);
    setToggleDetail(false);
    setToggleDelete(false);
    setToggleSync(false);
    setLoading(false);
    setDocumentTypeDisabled(false);
    fetchData();
    fetchMasterData();
  }, [refresh]);

  useEffect(() => {
    if (!isDetailMode) {
      for (const value of Object.values(documentTypeWarning)) {
        if (value !== null) {
          setDocumentTypeDisabled(true);
          return;
        }
      }
      setDocumentTypeDisabled(false);
    }
  }, [documentTypeWarning, isDetailMode]);

  const bgColorTemplate = (props) => {
    return (
      <div className="flex items-center gap-2">
        <div
          className="w-6 h-6 border border-gray-300 rounded"
          style={{ backgroundColor: `#${props.BgColor}` }}
        ></div>
        <span>#{props.BgColor}</span>
      </div>
    );
  };

  const orientationTemplate = (props) => {
    return (
      <span
        className={`px-2 py-1 rounded text-xs font-medium ${
          props.Orientation === "portrait"
            ? "bg-blue-100 text-blue-800"
            : "bg-green-100 text-green-800"
        }`}
      >
        {props.Orientation.charAt(0).toUpperCase() + props.Orientation.slice(1)}
      </span>
    );
  };

  const actionTemplate = (props) => {
    return (
      <div className="e-actionParent flex gap-2">
        <button className="e-action" onClick={() => handleDetail(props)}>
          <FaEye className="e-actionIcon" />
          <div>Detail</div>
        </button>
        <button className="e-action" onClick={() => handleEdit(props)}>
          <FaEdit className="e-actionIcon" />
          <div>Edit</div>
        </button>
        {/* <button
          className="e-action text-red-600"
          onClick={() => handleDelete(props)}
        >
          <FaTrash className="e-actionIcon" />
          <div>Delete</div>
        </button> */}
      </div>
    );
  };

  function onIntegratedPlatformFiltering(args) {
    let query = new Query();
    query =
      args.text !== ""
        ? query.where("text", "contains", args.text, true)
        : query;
    args.updateData(integratedPlatforms, query);
  }

  function onMembershipTypeFiltering(args) {
    let query = new Query();
    query =
      args.text !== ""
        ? query.where("text", "contains", args.text, true)
        : query;
    args.updateData(membershipTypes, query);
  }

  const handleAdd = () => {
    setIsDetailMode(false);
    resetForm();
    setToggleAdd(true);
  };

  const handleDetail = async (data) => {
    setIsDetailMode(true);
    await fetchDocumentTypeDetail(data.Uuid);
    setToggleDetail(true);
  };

  const handleEdit = async (data) => {
    setIsDetailMode(false);
    await fetchDocumentTypeDetail(data.Uuid);
    setToggleEdit(true);
  };

  const handleSync = () => {
    resetSyncForm();
    setToggleSync(true);
  };

  const handleDelete = async (data) => {
    await fetchDocumentTypeDetail(data.Uuid);
    setToggleDelete(true);
  };

  const queryCellInfo = (args) => {
    args.cell?.classList.add("e-cell");
  };

  const dataBound = () => {
    if (gridRef.current) {
      gridRef.current.autoFitColumns([
        "No",
        "Orientation",
        "BgColor",
        "Price",
        "ShowTimestamp",
        "CreatedAt",
        "Uuid",
      ]);
    }
  };

  const toolbarClick = (args) => {
    if (gridRef.current && args.item.id.includes("excelexport")) {
      const excelExportProperties = {
        fileName:
          "Paperless - Digitalisasi Untuk Negeri Document Types List.xlsx",
      };
      gridRef.current.excelExport(excelExportProperties);
    }
  };

  const resetForm = () => {
    setDocumentTypeData({
      uuid: null,
      membership_type_id: null,
      name: null,
      orientation: "portrait",
      bg_color: "ffffff",
      docSizeId: 3,
      price: 0,
      is_active: false,
      show_timestamp: false,
      jenis_order_id: null,
    });
    setDocumentTypeWarning({
      membership_type_id: null,
      name: null,
      orientation: null,
      docSizeId: null,
      price: null,
      is_active: null,
      show_timestamp: null,
      jenis_order_id: null,
    });
  };

  const resetSyncForm = () => {
    setSyncDocumentTypeData({
      integrated_platform_id: null,
    });
    setSyncDocumentTypeWarning({
      integrated_platform_id: null,
    });
  };

  const validateForm = () => {
    let isValid = true;
    const warnings = { ...documentTypeWarning };

    if (!documentTypeData.membership_type_id) {
      warnings.membership_type_id = "Membership Type is required";
      isValid = false;
    } else {
      warnings.membership_type_id = null;
    }

    if (!documentTypeData.name || documentTypeData.name.trim().length < 1) {
      warnings.name = "Name is required";
      isValid = false;
    } else {
      warnings.name = null;
    }

    if (!documentTypeData.orientation) {
      warnings.orientation = "Orientation is required";
      isValid = false;
    } else {
      warnings.orientation = null;
    }

    if (!documentTypeData.docSizeId) {
      warnings.docSizeId = "Document Size is required";
      isValid = false;
    } else {
      warnings.docSizeId = null;
    }

    if (documentTypeData.price < 0) {
      warnings.price = "Price cannot be negative";
      isValid = false;
    } else {
      warnings.price = null;
    }

    setDocumentTypeWarning(warnings);
    return isValid;
  };

  const handleSubmit = async (isEdit = false) => {
    if (!validateForm()) return;

    setLoading(true);

    try {
      const payload = {
        membership_type_id: documentTypeData.membership_type_id,
        name: documentTypeData.name,
        orientation: documentTypeData.orientation,
        bg_color: documentTypeData.bg_color,
        docSizeId: documentTypeData.docSizeId,
        is_active: documentTypeData.is_active,
        show_timestamp: documentTypeData.show_timestamp,
        jenis_order_id: documentTypeData.jenis_order_id || null,
        price: parseInt(documentTypeData.price),
      };

      if (isEdit) {
        await axios.put(
          API_ROOT + "/admin/document-types/update/" + documentTypeData.uuid,
          payload,
          {
            headers: { Authorization: getCookie("admin_auth") },
          },
        );
        toast.success("Document Type Updated!");
      } else {
        await axios.post(API_ROOT + "/admin/document-types/create", payload, {
          headers: { Authorization: getCookie("admin_auth") },
        });
        toast.success("Document Type Created!");
      }

      setToggleAdd(false);
      setToggleEdit(false);
      setRefresh((prev) => prev + 1);
    } catch (error) {
      toast.error(error.response?.data?.message ?? "Server Error");
      // toast.error(
      //   isEdit
      //     ? "Failed to update document type"
      //     : "Failed to create document type"
      // );

      if (error.response?.status === 401) {
        navigate("/login");
      }
    }

    setLoading(false);
  };

  const handleSyncSubmit = async () => {
    if (!syncDocumentTypeData.integrated_platform_id) {
      setSyncDocumentTypeWarning({
        integrated_platform_id: "Integrated Platform is required",
      });
      return;
    } else {
      setSyncDocumentTypeWarning({
        integrated_platform_id: null,
      });
    }

    setLoading(true);

    try {
      const payload = {
        integrated_platform_id: syncDocumentTypeData.integrated_platform_id,
      };

      await axios.post(API_ROOT + "/admin/document-types/sync", payload, {
        headers: { Authorization: getCookie("admin_auth") },
      });
      toast.success("Document Type Synced!");

      setToggleSync(false);
      setRefresh((prev) => prev + 1);
    } catch (error) {
      toast.error(error.response?.data?.message || "Server Error");

      if (error.response?.status === 401) {
        navigate("/login");
      }
    }

    setLoading(false);
  };

  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);

    try {
      await axios.delete(
        API_ROOT + "/admin/document-types/delete/" + documentTypeData.uuid,
        {
          headers: { Authorization: getCookie("admin_auth") },
        },
      );

      toast.success("Document Type Deleted!");
      setToggleDelete(false);
      setRefresh((prev) => prev + 1);
    } catch (error) {
      toast.error("Failed to delete document type!");

      if (error.response?.status === 401) {
        navigate("/login");
      }
    }

    setDeleteLoading(false);
  };

  const renderFormFields = () => (
    <div className="flex flex-col gap-4 w-fit">
      <div className="flex flex-col gap-2">
        <h4 className="font-medium">Integrated Platform *</h4>
        <DropDownListComponent
          allowFiltering
          filtering={onIntegratedPlatformFiltering}
          dataSource={integratedPlatforms}
          fields={{ text: "text", value: "value" }}
          placeholder="Select integrated platform"
          value={documentTypeData.integrated_platform_id}
          change={(e) => {
            if (e.value) {
              fetchMembershipTypesByIntegratedPlatform(e.value);
              setDocumentTypeData((prev) => ({
                ...prev,
                integrated_platform_id: e.value,
              }));
              setDocumentTypeWarning((prev) => ({
                ...prev,
                integrated_platform_id: null,
              }));
            } else {
              setDocumentTypeWarning((prev) => ({
                ...prev,
                integrated_platform_id: "Membership Type Required",
              }));
            }
          }}
          enabled={!isDetailMode && documentTypeData.integrated_platform_id}
          cssClass={documentTypeWarning.integrated_platform_id ? "e-error" : ""}
          width="400px"
        />
        {documentTypeWarning.integrated_platform_id && (
          <p className="text-xs text-red-500">
            {documentTypeWarning.integrated_platform_id}
          </p>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <h4 className="font-medium">Membership Type *</h4>
        <DropDownListComponent
          allowFiltering
          filtering={onMembershipTypeFiltering}
          dataSource={membershipTypes}
          fields={{ text: "text", value: "value" }}
          placeholder="Select membership type"
          value={documentTypeData.membership_type_id}
          change={(e) => {
            if (e.value) {
              setDocumentTypeData((prev) => ({
                ...prev,
                membership_type_id: e.value,
              }));
              setDocumentTypeWarning((prev) => ({
                ...prev,
                membership_type_id: null,
              }));
            } else {
              setDocumentTypeWarning((prev) => ({
                ...prev,
                membership_type_id: "Membership Type Required",
              }));
            }
          }}
          enabled={!isDetailMode && !!documentTypeData.integrated_platform_id}
          cssClass={documentTypeWarning.membership_type_id ? "e-error" : ""}
          width="400px"
        />
        {documentTypeWarning.membership_type_id && (
          <p className="text-xs text-red-500">
            {documentTypeWarning.membership_type_id}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="font-medium">Name *</h4>
        <input
          type="text"
          placeholder="Input name..."
          maxLength={100}
          spellCheck="false"
          className={
            "w-[400px] p-2 text-sm rounded-[4px] border focus:outline-2 " +
            (!documentTypeWarning.name
              ? "border-slate-500 focus:outline-sky-500"
              : "border-red-500 focus:outline-red-500")
          }
          value={documentTypeData.name || ""}
          disabled={isDetailMode}
          onChange={(e) => {
            setDocumentTypeData((prev) => ({ ...prev, name: e.target.value }));
            if (e.target.value.trim().length < 1) {
              setDocumentTypeWarning((prev) => ({
                ...prev,
                name: "Name is required",
              }));
            } else {
              setDocumentTypeWarning((prev) => ({ ...prev, name: null }));
            }
          }}
        />
        {documentTypeWarning.name && (
          <p className="text-xs text-red-500">{documentTypeWarning.name}</p>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="font-medium">Jenis Layanan</h4>
        <DropDownListComponent
          dataSource={[
            { text: "Tanpa jenis layanan", value: "" },
            ...jenisOrders,
          ]}
          fields={{ text: "text", value: "value" }}
          value={documentTypeData.jenis_order_id}
          change={(e) => {
            setDocumentTypeData((prev) => ({
              ...prev,
              jenis_order_id: e.value || false,
            }));
          }}
          enabled={!isDetailMode}
          width="400px"
        />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="font-medium">Orientation *</h4>
        <DropDownListComponent
          dataSource={orientationOptions}
          fields={{ text: "text", value: "value" }}
          placeholder="Select orientation"
          value={documentTypeData.orientation}
          change={(e) =>
            setDocumentTypeData((prev) => ({ ...prev, orientation: e.value }))
          }
          enabled={!isDetailMode}
          cssClass={documentTypeWarning.orientation ? "e-error" : ""}
          width="400px"
        />
        {documentTypeWarning.orientation && (
          <p className="text-xs text-red-500">
            {documentTypeWarning.orientation}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="font-medium">Background Color</h4>
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 border border-gray-300 rounded"
            style={{ backgroundColor: `#${documentTypeData.bg_color}` }}
          ></div>
          <input
            type="text"
            placeholder="ffffff"
            maxLength={6}
            className="w-[350px] p-2 text-sm rounded-[4px] border border-gray-300 bg-gray-100"
            value={documentTypeData.bg_color || "ffffff"}
            readOnly
          />
        </div>
        <p className="text-xs text-gray-500">Default: ffffff (white)</p>
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="font-medium">Document Size *</h4>
        <DropDownListComponent
          dataSource={docSizes}
          fields={{ text: "text", value: "value" }}
          placeholder="Select document size"
          value={documentTypeData.docSizeId}
          change={(e) =>
            setDocumentTypeData((prev) => ({ ...prev, docSizeId: e.value }))
          }
          enabled={!isDetailMode}
          cssClass={documentTypeWarning.docSizeId ? "e-error" : ""}
          width="400px"
        />
        {documentTypeWarning.docSizeId && (
          <p className="text-xs text-red-500">
            {documentTypeWarning.docSizeId}
          </p>
        )}
        <p className="text-xs text-gray-500">Default: 3 (A4)</p>
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="font-medium">Price</h4>
        <div className="flex flex-nowrap gap-2">
          <span className="text-xs pt-1">Rp. </span>
          <NumericTextBoxComponent
            placeholder="0"
            value={documentTypeData.price}
            change={(e) =>
              setDocumentTypeData((prev) => ({ ...prev, price: e.value || 0 }))
            }
            enabled={!isDetailMode}
            currency="IDR"
            min={0}
            width="400px"
            cssClass={documentTypeWarning.price ? "e-error" : ""}
          />
        </div>
        {documentTypeWarning.price && (
          <p className="text-xs text-red-500">{documentTypeWarning.price}</p>
        )}
        <p className="text-xs text-gray-500">Default: 0</p>
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="font-medium">Show Timestamp *</h4>
        <DropDownListComponent
          dataSource={[
            { text: "Show", value: true },
            { text: "Hide", value: false },
          ]}
          fields={{ text: "text", value: "value" }}
          value={documentTypeData.show_timestamp}
          change={(e) => {
            setDocumentTypeData((prev) => ({
              ...prev,
              show_timestamp: e.value || false,
            }));
          }}
          enabled={!isDetailMode}
          width="400px"
        />
      </div>

      <div className="flex flex-col gap-2">
        <h4 className="font-medium">Is Active *</h4>
        <DropDownListComponent
          dataSource={[
            { text: "Active", value: true },
            { text: "Inactive", value: false },
          ]}
          fields={{ text: "text", value: "value" }}
          value={documentTypeData.is_active}
          change={(e) => {
            setDocumentTypeData((prev) => ({
              ...prev,
              is_active: e.value || false,
            }));
          }}
          enabled={!isDetailMode}
          width="400px"
        />
      </div>
    </div>
  );

  const renderSyncFormFields = () => (
    <div className="flex flex-col gap-2">
      <h4 className="font-medium">Integrated Platform *</h4>
      <DropDownListComponent
        allowFiltering
        filtering={onIntegratedPlatformFiltering}
        dataSource={integratedPlatforms}
        fields={{ text: "text", value: "value" }}
        placeholder="Select integrated platform"
        value={syncDocumentTypeData.integrated_platform_id}
        change={(e) => {
          if (e.value) {
            setSyncDocumentTypeData({
              integrated_platform_id: e.value,
            });
            setSyncDocumentTypeWarning({
              integrated_platform_id: null,
            });
          } else {
            setSyncDocumentTypeWarning({
              integrated_platform_id: "Integrated Platform Required",
            });
          }
        }}
        enabled={!syncDocumentTypeWarning.integrated_platform_id}
        cssClass={
          syncDocumentTypeWarning.integrated_platform_id ? "e-error" : ""
        }
        width="400px"
      />
      {syncDocumentTypeWarning.integrated_platform_id && (
        <p className="text-xs text-red-500">
          {syncDocumentTypeWarning.integrated_platform_id}
        </p>
      )}
    </div>
  );

  return pageLoading ? (
    <PageLoading />
  ) : (
    <div className="m-2 md:m-10 mt-24 px-2 py-10 md:p-10 bg-white rounded-3xl">
      <div className="flex justify-between items-end pb-4">
        <Header title="Document Types Data" />
        <div className="flex flex-wrap gap-2">
          <button
            style={{ backgroundColor: currentColor }}
            className="text-white py-2 px-4 rounded-md text-sm flex items-center gap-2 justify-center flex-nowrap"
            onClick={handleSync}
          >
            <FaSync className="w-3 h-3" />
            <div className="text-nowrap">Sync Document Type</div>
          </button>
          <button
            style={{ backgroundColor: currentColor }}
            className="text-white py-2 px-4 rounded-md text-sm flex items-center gap-2 justify-center flex-nowrap"
            onClick={handleAdd}
          >
            <FaPlus className="w-3 h-3" />
            <div className="text-nowrap">Add Document Type</div>
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[1800px] cursor-pointer">
          <GridComponent
            dataSource={documentTypesList}
            allowPaging
            allowSorting
            allowTextWrap={false}
            pageSettings={{
              pageCount: 5,
              pageSizes: ["All", "10", "25", "50"],
            }}
            textWrapSettings={{ wrapMode: "Content" }}
            toolbar={["Search", "ExcelExport"]}
            queryCellInfo={queryCellInfo}
            allowExcelExport={true}
            toolbarClick={toolbarClick}
            dataBound={dataBound}
            ref={gridRef}
          >
            <ColumnsDirective>
              <ColumnDirective
                field="No"
                headerText="No"
                textAlign="Center"
                width={50}
                type="Number"
              />
              <ColumnDirective
                field="Uuid"
                headerText="Action"
                textAlign="Center"
                isPrimaryKey={true}
                template={actionTemplate}
                minWidth={150}
              />
              <ColumnDirective
                field="IntegratedPlatformName"
                headerText="Integrated Platform"
                minWidth={150}
              />
              <ColumnDirective
                field="MembershipTypeName"
                headerText="Membership Type"
                minWidth={150}
              />
              <ColumnDirective
                field="KeyDoc"
                headerText="Key Document"
                autoFit
              />
              <ColumnDirective field="Name" headerText="Name" minWidth={450} />
              <ColumnDirective
                field="JenisLayanan"
                headerText="Jenis Layanan"
                textAlign="Left"
                minWidth={100}
                template={(props) => (
                  <div className="text-left">{props.JenisLayanan || "-"}</div>
                )}
              />
              <ColumnDirective
                field="Orientation"
                headerText="Orientation"
                template={orientationTemplate}
                textAlign="Center"
                minWidth={100}
              />
              <ColumnDirective
                field="BgColor"
                headerText="Background Color"
                template={bgColorTemplate}
                textAlign="Center"
                minWidth={150}
              />
              <ColumnDirective
                field="DocSizeName"
                headerText="Document Size"
                minWidth={10}
              />
              <ColumnDirective
                field="Price"
                headerText="Price"
                textAlign="Right"
                minWidth={10}
              />
              <ColumnDirective
                field="ShowTimestamp"
                headerText="Show Timestamp"
                textAlign="Center"
                width={120}
                template={(props) => (
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      props.ShowTimestamp
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {props.ShowTimestamp ? "Show" : "Hide"}
                  </span>
                )}
              />
              <ColumnDirective
                field="IsActive"
                headerText="Is Active"
                textAlign="Center"
                width={120}
                template={(props) => (
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      props.IsActive
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {props.IsActive ? "Active" : "Inactive"}
                  </span>
                )}
              />
              <ColumnDirective
                field="CreatedAt"
                headerText="Created At"
                width="auto"
              />
            </ColumnsDirective>
            <Inject
              services={[Search, Toolbar, Page, Sort, Resize, ExcelExport]}
            />
          </GridComponent>
        </div>
      </div>

      {/* Add Modal */}
      {toggleAdd && (
        <div className="overflow-auto absolute z-[99] top-0 left-0 flex justify-center w-full min-h-screen h-full overflow-y-auto py-40 px-[100px] bg-slate-800/50">
          <div className="bg-white flex flex-col gap-2 w-full max-w-[464px] h-fit rounded-xl p-8">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-medium">Add Document Type</h1>
              <div
                className="cursor-pointer p-2"
                onClick={() => {
                  setToggleAdd(false);
                  resetForm();
                }}
              >
                <FaXmark className="w-4 h-4" />
              </div>
            </div>
            <hr />

            {renderFormFields()}

            <div className="flex justify-end items-center gap-2 mt-6">
              <button
                className="text-gray-600 w-24 py-2 px-4 rounded-md text-sm border border-gray-300"
                onClick={() => {
                  setToggleAdd(false);
                  resetForm();
                }}
              >
                Cancel
              </button>
              <button
                disabled={documentTypeDisabled || loading}
                style={{ backgroundColor: currentColor }}
                className={
                  "text-white py-2 px-4 rounded-md text-sm " +
                  (documentTypeDisabled || loading
                    ? "cursor-not-allowed opacity-50"
                    : "cursor-pointer")
                }
                onClick={() => handleSubmit(false)}
              >
                {!loading ? "Add Document Type" : "Loading..."}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {toggleEdit && (
        <div className="overflow-auto absolute z-[99] top-0 left-0 flex justify-center w-full min-h-screen h-full overflow-y-auto py-40 px-[100px] bg-slate-800/50">
          <div className="bg-white flex flex-col gap-2 w-full max-w-[464px] h-fit rounded-xl p-8">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-medium">Edit Document Type</h1>
              <div
                className="cursor-pointer p-2"
                onClick={() => {
                  setToggleEdit(false);
                  resetForm();
                }}
              >
                <FaXmark className="w-4 h-4" />
              </div>
            </div>
            <hr />

            {renderFormFields()}

            <div className="flex justify-end items-center gap-2 mt-6">
              <button
                className="text-gray-600 w-24 py-2 px-4 rounded-md text-sm border border-gray-300"
                onClick={() => {
                  setToggleEdit(false);
                  resetForm();
                }}
              >
                Cancel
              </button>
              <button
                disabled={documentTypeDisabled || loading}
                style={{ backgroundColor: currentColor }}
                className={
                  "text-white py-2 px-4 rounded-md text-sm " +
                  (documentTypeDisabled || loading
                    ? "cursor-not-allowed opacity-50"
                    : "cursor-pointer")
                }
                onClick={() => handleSubmit(true)}
              >
                {!loading ? "Update Document Type" : "Loading..."}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sync Modal */}
      {toggleSync && (
        <div className="overflow-auto absolute z-[99] top-0 left-0 flex justify-center w-full min-h-screen h-full overflow-y-auto py-40 px-[100px] bg-slate-800/50">
          <div className="bg-white flex flex-col gap-2 w-full max-w-[464px] h-fit rounded-xl p-8">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-medium">Sync Document Type</h1>
              <div
                className="cursor-pointer p-2"
                onClick={() => {
                  setToggleSync(false);
                  resetSyncForm();
                }}
              >
                <FaXmark className="w-4 h-4" />
              </div>
            </div>
            <hr />

            {renderSyncFormFields()}

            <div className="flex justify-end items-center gap-2 mt-6">
              <button
                className="text-gray-600 w-24 py-2 px-4 rounded-md text-sm border border-gray-300"
                onClick={() => {
                  setToggleSync(false);
                  resetSyncForm();
                }}
              >
                Cancel
              </button>
              <button
                disabled={
                  syncDocumentTypeWarning.integrated_platform_id || loading
                }
                style={{ backgroundColor: currentColor }}
                className={
                  "text-white py-2 px-4 rounded-md text-sm " +
                  (syncDocumentTypeWarning.integrated_platform_id || loading
                    ? "cursor-not-allowed opacity-50"
                    : "cursor-pointer")
                }
                onClick={() => handleSyncSubmit()}
              >
                {!loading ? "Sync Document Type" : "Loading..."}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {toggleDetail && (
        <div className="overflow-auto absolute z-[99] top-0 left-0 flex justify-center w-full min-h-screen h-full overflow-y-auto py-40 px-[100px] bg-slate-800/50">
          <div className="bg-white flex flex-col gap-2 w-full max-w-[464px] h-fit rounded-xl p-8">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-medium">Document Type Details</h1>
              <div
                className="cursor-pointer p-2"
                onClick={() => {
                  setToggleDetail(false);
                  resetForm();
                }}
              >
                <FaXmark className="w-4 h-4" />
              </div>
            </div>
            <hr />

            {renderFormFields()}

            <div className="flex justify-end items-center mt-6">
              <button
                className="text-gray-600 w-24 py-2 px-4 rounded-md text-sm border border-gray-300"
                onClick={() => {
                  setToggleDetail(false);
                  resetForm();
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {toggleDelete && (
        <div className="overflow-auto absolute z-[99] top-0 left-0 flex justify-center w-full min-h-screen h-full overflow-y-auto py-40 px-[100px] bg-slate-800/50">
          <div className="bg-white flex flex-col gap-4 w-full max-w-[400px] h-fit rounded-xl p-8">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-medium">Delete Document Type</h1>
              <div
                className="cursor-pointer p-2"
                onClick={() => {
                  setToggleDelete(false);
                  resetForm();
                }}
              >
                <FaXmark className="w-4 h-4" />
              </div>
            </div>
            <hr />

            <div className="text-center">
              <FaTrash className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-lg font-semibold mb-2">Are you sure?</h2>
              <p className="text-gray-600 mb-4">
                Do you really want to delete the document type "
                {documentTypeData.name}"? This action cannot be undone.
              </p>
            </div>

            <div className="flex justify-center items-center gap-2 mt-4">
              <button
                className="text-gray-600 w-24 py-2 px-4 rounded-md text-sm border border-gray-300"
                onClick={() => {
                  setToggleDelete(false);
                  resetForm();
                }}
              >
                Cancel
              </button>
              <button
                disabled={deleteLoading}
                className={
                  "bg-red-500 text-white w-24 py-2 px-4 rounded-md text-sm " +
                  (deleteLoading
                    ? "cursor-not-allowed opacity-50"
                    : "cursor-pointer")
                }
                onClick={handleDeleteConfirm}
              >
                {!deleteLoading ? "Delete" : "Deleting..."}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentTypes;
